import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles } from 'lucide-react'
import { usePermission } from '@/hooks/usePermission'
import { AssistantPanel } from './AssistantPanel'

/**
 * The top bar's entry point to the assistant.
 *
 * <p>Renders nothing at all for somebody without {@code assistant:use}. Not a disabled
 * button: an icon that cannot be pressed still advertises a feature, and the same permission
 * is what the endpoint checks anyway.
 */
export function AssistantLauncher() {
  const { t } = useTranslation()
  const allowed = usePermission('assistant:use')
  const [open, setOpen] = useState(false)

  // A keyboard route in, because this is a panel people open dozens of times a day and
  // reaching for the mouse each time is the thing they stop doing after a week.
  useEffect(() => {
    if (!allowed) return undefined
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === '/' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setOpen((shown) => !shown)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [allowed])

  if (!allowed) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('assistant.open')}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="grid size-9 place-items-center rounded-full text-[var(--color-body)] hover:bg-[var(--color-page)]"
      >
        <Sparkles className="size-5" aria-hidden="true" />
      </button>
      <AssistantPanel open={open} onOpenChange={setOpen} />
    </>
  )
}
